<template>
  <div class="tv-calendar" :data-zoom-level="$zoomLevel">
    <div class="calendar-header">
      <div class="left-header-div">
        <img alt="logo" src="/images/opendock_graphic_dark.svg" class="logo" />
        <div class="today-date">
          <v-row>
            <v-col>
              <v-btn variant="outlined" color="grey-darken-2" @click="setToday()">Today</v-btn>
              <v-btn variant="text" @click="prev">
                <v-icon>mdi-chevron-left</v-icon>
              </v-btn>
              <v-btn variant="text" @click="next">
                <v-icon>mdi-chevron-right</v-icon>
              </v-btn>
              <v-icon class="ml-1" size="x-small" title="Lock on current date">
                {{ lockDateIcon }}
              </v-icon>
              <v-icon class="ml-1" size="x-small" v-if="autoScroll" title="Follow current time">
                mdi-update
              </v-icon>
              {{ formattedHeaderDate }}
            </v-col>
          </v-row>
          <div class="warehouse-name pt-6">{{ title }}</div>
        </div>
      </div>
      <div class="right-header-div">
        <div class="d-flex">
          <span class="today-date">{{ currentTime }}</span>
          <span class="mx-3 d-flex">
            <zoom-buttons :store-in-local-storage="false">
              <template #buttons="{ zoomIn, zoomOut }">
                <div class="d-flex flex-row">
                  <v-btn variant="text" @click="zoomIn">
                    <v-icon>mdi-magnify-plus</v-icon>
                  </v-btn>
                  <v-btn variant="text" @click="zoomOut">
                    <v-icon>mdi-magnify-minus</v-icon>
                  </v-btn>
                </div>
              </template>
            </zoom-buttons>

            <v-menu absolute :close-on-content-click="false">
              <template v-slot:activator="{ props }">
                <v-btn variant="text" size="x-large" v-bind="props">
                  <v-icon>mdi-cog</v-icon>
                </v-btn>
              </template>
              <v-list density="compact" nav>
                <v-list-item>
                  <v-checkbox
                    hide-details
                    density="compact"
                    label="Follow current time"
                    v-model="autoScroll" />
                </v-list-item>
                <v-list-item>
                  <v-checkbox
                    density="compact"
                    hide-details
                    label="Lock on current date"
                    v-model="lockCurrentDate" />
                </v-list-item>
                <v-divider />
                <v-list-item>
                  <v-checkbox
                    density="compact"
                    hide-details
                    label="Show appointment tags"
                    v-model="showAppointmentTags" />
                </v-list-item>
                <v-list-item>
                  <v-checkbox
                    hide-details
                    density="compact"
                    label="Show appointment fields"
                    v-model="showAppointmentFields" />
                </v-list-item>
                <v-list-item>
                  <v-checkbox
                    hide-details
                    density="compact"
                    label="Show parallel capacity"
                    v-model="showParallelCapacity" />
                </v-list-item>
              </v-list>
            </v-menu>
          </span>
        </div>
        <div class="warehouse-name">
          {{ formatTimezoneString() }}
        </div>
      </div>
    </div>
    <div class="calendar-wrapper">
      <FullCalendar
        v-if="calendarOptions.timeZone"
        ref="fullCalendar"
        :options="calendarOptions"
        :data-zoom-level="$zoomLevel"
        :data-interval-setting="$intervalMinutes"
        :resourceAreaWidth="'auto'">
        <template v-slot:event-content="{ event }">
          <event-tv-mode
            :event="event"
            :showAppointmentFields="showAppointmentFields"
            :timezone="warehouse?.timezone"
            :showAppointmentTags="showAppointmentTags"></event-tv-mode>
        </template>
      </FullCalendar>
    </div>

    <v-footer app bottom>
      <span class="text">
        <strong>{{ totalAppointments }} scheduled - {{ cancelledAppointments }} cancelled</strong>
      </span>
    </v-footer>
  </div>
</template>

<script>
import moment from 'moment-timezone';

import gridMixin from '@/modules/calendar/mixins/gridMixin';
import config from '@/config/config';
import { AppointmentType } from '@nova/core';

import FullCalendar from '@fullcalendar/vue3';
import luxonPlugin from '@fullcalendar/luxon2';
import dayGridPlugin from '@fullcalendar/daygrid';
import interactionPlugin from '@fullcalendar/interaction';
import resourceTimeGridPlugin from '@fullcalendar/resource-timegrid';
import scrollGrid from '@fullcalendar/scrollgrid';
import { DateTime } from 'luxon';
import { waitForElement } from '@satellite/plugins/util.js';

export default {
  name: 'AppointmentsTVModePage',
  mixins: [gridMixin],
  components: {
    FullCalendar // Register the component here
  },
  data() {
    return {
      categories: [],
      title: '',
      autoScroll: true,
      lockCurrentDate: true,
      showAppointmentTags: true,
      showAppointmentFields: true,
      showParallelCapacity: false,
      ready: false,
      currentTime: null,
      timeIndicatorObserver: null,
      refreshIntervalSeconds: 10,
      currentTimeInterval: null,
      warehouse: null,
      dockIds: [],
      warehouseId: null
    };
  },
  computed: {
    timezone() {
      return this.warehouse?.timezone;
    },
    calendarOptions() {
      return {
        allDaySlot: false,
        contentHeight: 'auto',
        eventClassNames: function (arg) {
          const isReserve = arg.event.extendedProps?.type === AppointmentType.Reserve;
          let classes = [];
          if (arg.event.extendedProps?.status && !isReserve) {
            classes.push(`bg-${arg.event.extendedProps.status.toLowerCase()}`);
          }
          if (isReserve) {
            classes.push('bg-reserve');
          }
          return classes;
        },
        expandRows: true,
        footerToolbar: false,
        headerToolbar: false,
        nowIndicator: true,
        slotMaxTime: '23:59:00',
        slotMinTime: '00:00:00',
        schedulerLicenseKey: config.fullcalendar_key,
        slotLabelContent: interval => this.formatInterval(interval),
        slotLabelInterval: '00:30',
        initialView: 'resourceTimeGridDay',
        plugins: [
          luxonPlugin,
          dayGridPlugin,
          interactionPlugin,
          resourceTimeGridPlugin,
          scrollGrid
        ],
        dayMinWidth: 150,
        events: this.appointments,
        resourceOrder: 'order',
        resources: this.categories,
        slotEventOverlap: false,
        timeZone: this.timezone,
        viewDidMount: async () => {
          this.addCustomClassesToSlotLabels();
          this.updateTime();
          if (this.isSameDay(this.selectedDate, this.today())) {
            await waitForElement('.fc-timegrid-now-indicator-line');
            await this.scrollToTime();
          }

          this.ready = true;
        }
      };
    },
    firstCategory() {
      return this.warehouse?.docks[0].id;
    },
    appointments() {
      return this.$warehouseEventsByDock(this.warehouseId, this.dockIds)
        .filter(event => event?.status !== this.novaCore.AppointmentStatus.Cancelled)
        .map(event => {
          return {
            ...event,
            resourceId: this.showParallelCapacity
              ? event.dockId
              : event.parentDockId ?? event.dockId
          };
        });
    },
    totalAppointments() {
      return (
        this.$warehouseEventsByDock(this.warehouseId, this.dockIds)?.filter(event => {
          return (
            !this.novaCore.isReserve(event) &&
            event?.status !== this.novaCore.AppointmentStatus.Cancelled
          );
        }).length || 0
      );
    },
    cancelledAppointments() {
      return (
        this.$warehouseEventsByDock(this.warehouseId, this.dockIds)?.filter(event => {
          return (
            !this.novaCore.isReserve(event) &&
            event?.status === this.novaCore.AppointmentStatus.Cancelled
          );
        }).length || 0
      );
    },
    lockDateIcon() {
      return this.lockCurrentDate ? 'mdi-lock' : 'mdi-lock-open';
    },
    calendarApi() {
      return this.$refs.fullCalendar ? this.$refs.fullCalendar.getApi() : null;
    },
    canSetTimezoneOnCalendar() {
      return Boolean(this.warehouse?.timezone && this.calendarApi);
    },
    selectedDate: {
      get() {
        return this.$selectedDate;
      },
      set(date) {
        // Make sure date is a Date ISO String
        return this.$store.dispatch('Calendar/setSelectedDate', {
          value: date,
          getEvents: this.ready
        });
      }
    },
    formattedHeaderDate() {
      return DateTime.fromISO(this.selectedDate).setZone(this.warehouse?.timezone).toFormat('DD');
    }
  },
  methods: {
    isSameDay(d1, d2) {
      const date1 = new Date(d1);
      const date2 = new Date(d2);
      return (
        date1.getUTCFullYear() === date2.getUTCFullYear() &&
        date1.getUTCMonth() === date2.getUTCMonth() &&
        date1.getUTCDate() === date2.getUTCDate()
      );
    },
    isReserve(event) {
      return this.novaCore.isReserve(event);
    },
    async setToday() {
      this.selectedDate = this.today();

      await this.calendarApi.gotoDate(this.selectedDate);
      this.lockCurrentDate = true;
      await this.scrollToTime();
    },
    async prev() {
      this.selectedDate = DateTime.fromISO(this.selectedDate).minus({ days: 1 }).toISO();
      await this.calendarApi.gotoDate(this.selectedDate);
      this.lockCurrentDate = this.isSameDay(this.selectedDate, this.today());
    },
    async next() {
      this.selectedDate = DateTime.fromISO(this.selectedDate).plus({ days: 1 }).toISO();
      await this.calendarApi.gotoDate(this.selectedDate);
      this.lockCurrentDate = this.isSameDay(this.selectedDate, this.today());
    },
    getSortedCategories() {
      if (!this.warehouse) {
        return [];
      }
      const dockIds = this.$route.query?.dockIds;
      const docks = this.warehouse.docks.filter(dock => dockIds.includes(dock.id));
      let sortedDocks = this.novaCore.naturalSort(docks, false, 'name');
      sortedDocks = this.novaCore.naturalSort(sortedDocks, false, 'sortOrder');

      return sortedDocks
        .filter(dock => {
          if (!this.showParallelCapacity) {
            return !this.novaCore.isCapacityChild(dock);
          }
          return true;
        })
        .map((dock, index) => {
          return { ...dock, title: dock.name, order: index };
        });
    },
    async getAppointments() {
      if (!this.warehouseId || !this.dockIds) {
        this.notify(
          'Invalid warehouse/docks! Please go back to the calendar view and open the TV Mode again.',
          'error'
        );
        return;
      }
      const warehouse = await this.services.warehouse.getWarehouseById(
        this.warehouseId,
        {},
        this.services.warehouse.requestOptions.grid
      );
      if (warehouse) {
        this.warehouse = warehouse;
        await this.$store.dispatch('Calendar/setSelectedWarehouse', warehouse);
        await this.$store.dispatch(
          'Calendar/setSelectedDocks',
          warehouse.docks.filter(dock => this.dockIds.includes(dock.id))
        );
        await this.$store.dispatch('Calendar/setViewType', {
          value: 'day',
          getEvents: true
        });

        this.categories = this.getSortedCategories();

        this.title = `${warehouse.name}`;
        await this.$store.dispatch('Calendar/getEvents');
      }
    },
    formatTimeToWarehouseTimezone() {
      if (this.warehouse?.timezone) {
        return this.novaCore.formatDateTimeWithMilitarySupport(
          new Date().toISOString(),
          this.warehouse.timezone,
          this.novaCore.LuxonDateTimeFormats.Extended12HrTimeAMPM,
          this.$isMilitaryTimeEnabled(this.$selectedWarehouse),
          this.novaCore.LuxonDateTimeFormats.Extended24HrTime
        );
      }
    },
    formatTimezoneString() {
      if (this.warehouse?.timezone) {
        return `${this.warehouse.timezone} (${moment()
          .tz(this.warehouse.timezone)
          .format(this.novaCore.DateTimeFormats.TimezoneOffset)})`;
      }
    },
    today(returnDateTime = false) {
      if (this.warehouse?.timezone) {
        const dateTime = DateTime.now().setZone(this.warehouse.timezone);
        return returnDateTime ? dateTime : dateTime.toISO();
      }
      return returnDateTime ? DateTime.now() : DateTime.now().toISO();
    },
    updateTime() {
      if (this.calendarApi) {
        clearInterval(this.currentTimeInterval);
        this.currentTimeInterval = setInterval(() => {
          if (this.autoScroll) {
            this.scrollToTime();
          }

          if (this.lockCurrentDate && !this.isSameDay(this.selectedDate, this.today())) {
            this.$store.dispatch('Calendar/setSelectedDate', {
              value: this.today(),
              getEvents: true
            });
          }

          this.currentTime = this.formatTimeToWarehouseTimezone();
        }, this.refreshIntervalSeconds * 1000);
      }
    },
    async scrollToTime() {
      if (this.calendarApi) {
        const currentTimeElement = document.querySelector('.fc-timegrid-now-indicator-line');
        const scrollParent = this.calendarApi.el;

        if (currentTimeElement) {
          const childRect = currentTimeElement.getBoundingClientRect();
          const parentRect = scrollParent.getBoundingClientRect();
          const parentScrollTop = scrollParent.scrollTop;
          const relativeY = childRect.top - parentRect.top + parentScrollTop;
          const offset = parentRect.height / 2;

          scrollParent.scrollTo({
            top: relativeY - offset,
            behavior: 'smooth'
          });
        }
      }
    },
    addCustomClassesToSlotLabels() {
      this.$nextTick(() => {
        // Select all slot elements
        const slotElements = document.querySelectorAll('.fc-timegrid-slot');

        slotElements.forEach(function (slot) {
          const time = slot.getAttribute('data-time'); // e.g., "09:15:00"
          // Example: Add a class to every "inner slot" (20 min, 15 min) that falls between the hour
          // So we can style them differently
          const minutes = parseInt(time.split(':')[1], 10);
          if (minutes !== 0 && (minutes % 15 === 0 || minutes % 20 === 0)) {
            slot.classList.add('inner-slot');
          }
        });
      });
    }
  },
  beforeUnmount() {
    this.$eventHub.$off('update-Dock');
    ['update', 'create', 'delete'].forEach(operation =>
      this.$eventHub.$off(`${operation}-Appointment`)
    );
  },
  async mounted() {
    // Hide zendesk
    if (window.zE) {
      window.zE('messenger', 'hide');
    }

    this.warehouseId = this.$route.query['warehouseId'];
    this.dockIds = this.$route.query['dockIds'];

    ['update', 'create', 'delete'].forEach(operation => {
      this.$eventHub.$on(`${operation}-Appointment`, async payload => {
        await this.$store.dispatch('Appointments/handleSocketEvent', {
          action: operation,
          data: payload
        });
      });
    });

    if (this.$org) {
      this.mixpanel.track(this.mixpanel.events.VIEW.TV_MODE, {
        'Org Name': this.$org.name,
        'Org ID': this.$org.id
      });
    }

    await this.getAppointments();
    this.currentTime = this.formatTimeToWarehouseTimezone();
  },
  beforeMount() {
    this.$store.dispatch('Calendar/setZoomLevel', { zoom: 110, storeInLocalStorage: false });
  },
  watch: {
    lockCurrentDate() {
      this.updateTime();
    },
    autoScroll() {
      this.updateTime();
    },
    showParallelCapacity() {
      this.getAppointments();
    },
    '$org.customTags'() {
      this.$store.dispatch('Calendar/getEvents', {});
    },
    $zoomLevel() {
      this.$nextTick(() => {
        // Rerenders current date
        this.calendarApi?.gotoDate(this.selectedDate);
      });
    }
  }
};
</script>

<style lang="scss" scoped>
.warehouse-name {
  font-family: Barlow, sans-serif;
  font-size: 25px;
  font-style: normal;
  font-weight: 400;
  line-height: 30px;
  letter-spacing: 0;
  padding: 10px 0;
}

.today-date {
  font-family: Poppins, sans-serif;
  font-size: 24px;
  font-style: normal;
  font-weight: 600;
  line-height: 24px;
  letter-spacing: 0;
}

.right-header-div {
  text-align: right;
}

.left-header-div {
  text-align: left;
  display: flex;
}

.tv-calendar {
  .calendar-header {
    position: fixed;
    height: 95px;
    padding: 10px 10px 10px 10px;
    background: #ddebf0;
    display: flex;
    justify-content: space-between;

    top: 0;
    width: 100%;
    z-index: 100 !important;
  }

  .calendar-header .v-btn {
    height: 20px !important;
  }
}

.logo {
  height: 42px;
  margin-top: 5px;
  margin-left: 15px;
  margin-right: 30px;
}

:deep(.fc-timegrid-slot-label) {
  line-height: 80px;
  height: 80px !important;
}

:deep(.fc-timegrid-cols),
:deep(.fc-timegrid-slots) {
  tr {
    height: 80px;
  }
}

:deep(.fc-media-screen) {
  padding-top: 94px;
  height: calc(100vh - 40px);
}

:deep(.fc-scrollgrid-sync-inner) {
  font-size: 20px !important;
}

:deep(.fc-col-header) {
  min-width: 100% !important;
}

:deep(td.fc-resource:nth-child(even)) {
  background: rgba(188, 206, 212, 0.2) !important;
}

:deep(.fc-timegrid-slot-label-cushion) {
  transform: translateY(-52%);
  min-width: 90px;
  padding-right: 5px;
}

:deep(.fc-timegrid-slot-label) {
  font-size: 20px;

  &.inner-slot {
    font-size: 15px;
  }
}

:deep(.fc-timegrid-axis-frame) {
  background-color: #ddebf0;
}

:deep(th.fc-resource) {
  background-color: #bcced4 !important;
}

:deep(.fc-timegrid-body) {
  width: 100% !important;

  table {
    width: 100% !important;
  }
}

:deep(.calendar-wrapper .fc-event) {
  &.bg-requested {
    border: none !important;
  }
}

.calendar-wrapper {
  padding-bottom: 70px;
  margin-bottom: 0;
  height: 100vh;
}

:deep(.fc-col-header-cell-cushion) {
  overflow-wrap: break-word;
}

:deep(.fc-timegrid-now-indicator-arrow) {
  border-width: 10px 0px 10px 10px;
  transform: translateY(-3px);
  left: 81px;
}

:deep(.fc-timegrid-now-indicator-line) {
  border-width: 2px;
  opacity: 0.5;
}

[data-zoom-level='100'] {
  :deep(.fc-timegrid-slot-label) {
    line-height: 35px;
    height: 35px !important;
  }

  :deep(.fc-timegrid-cols),
  :deep(.fc-timegrid-slots) {
    tr {
      height: 35px;
    }
  }
}

[data-zoom-level='105'] {
  :deep(.fc-timegrid-slot-label) {
    line-height: 66px;
    height: 66px !important;
  }

  :deep(.fc-timegrid-cols),
  :deep(.fc-timegrid-slots) {
    tr {
      height: 66px;
    }
  }
}

[data-zoom-level='110'] {
  :deep(.fc-timegrid-slot-label) {
    line-height: 96px;
    height: 96px !important;
  }

  :deep(.fc-timegrid-cols),
  :deep(.fc-timegrid-slots) {
    tr {
      height: 96px;
    }
  }
}

[data-zoom-level='115'] {
  :deep(.fc-timegrid-slot-label) {
    line-height: 126px;
    height: 126px !important;
  }

  :deep(.fc-timegrid-cols),
  :deep(.fc-timegrid-slots) {
    tr {
      height: 126px;
    }
  }
}

[data-zoom-level='120'] {
  :deep(.fc-timegrid-slot-label) {
    line-height: 156px;
    height: 156px !important;
  }

  :deep(.fc-timegrid-cols),
  :deep(.fc-timegrid-slots) {
    tr {
      height: 156px;
    }
  }
}

[data-zoom-level='125'] {
  :deep(.fc-timegrid-slot-label) {
    line-height: 186px;
    height: 186px !important;
  }

  :deep(.fc-timegrid-cols),
  :deep(.fc-timegrid-slots) {
    tr {
      height: 186px;
    }
  }
}

[data-zoom-level='130'] {
  :deep(.fc-timegrid-slot-label) {
    line-height: 200px;
    height: 200px !important;
  }

  :deep(.fc-timegrid-cols),
  :deep(.fc-timegrid-slots) {
    tr {
      height: 200px;
    }
  }
}

:deep(#zoom-buttons) {
  display: inline;
  width: 110px;
  margin-right: 20px;
  margin-top: -3px;

  .zoom-label {
    font-weight: bold;
    color: $black !important;
  }

  button {
    border: none;
    border-radius: 4px !important;

    &:first-child {
      margin-right: 5px;
    }
  }
}
</style>
